import { Controller } from '@hotwired/stimulus'
import Cropper from 'cropperjs'
import AttachmentManagerItemController from './attachment-manager-item'

export interface AttachEvent extends Event {
  detail: {
    file: File
  }
}

export default class CropperController extends Controller<HTMLElement> {
  declare readonly attachmentManagerItemOutlet: AttachmentManagerItemController
  declare readonly sourceTarget: HTMLImageElement & { cropper?: Cropper }
  declare readonly sourceContainerTarget: HTMLElement
  declare readonly placeholderTarget: HTMLElement
  declare readonly initialStateValue: Record<string, number>
  declare readonly cropXTarget: HTMLInputElement
  declare readonly cropYTarget: HTMLInputElement
  declare readonly cropWTarget: HTMLInputElement
  declare readonly cropHTarget: HTMLInputElement

  declare readonly hasSourceTarget: boolean
  declare readonly hasSourceContainerTarget: boolean
  declare readonly hasPlaceholderTarget: boolean
  declare readonly hasInitialStateValue: boolean
  declare readonly hasInputSourceSelectorValue: boolean
  declare readonly hasCropXTarget: boolean
  declare readonly hasCropYTarget: boolean
  declare readonly hasCropWTarget: boolean
  declare readonly hasCropHTarget: boolean

  static targets = ['source', 'sourceContainer', 'placeholder', 'cropX', 'cropY', 'cropW', 'cropH']
  static values = {
    initialState: Object,
    inputSourceSelector: String,
  }
  static outlets = ['attachment-manager-item']

  cropper: Cropper
  connect() {
    if (this.hasSourceTarget) {
      this.sourceTarget.classList.add('block', 'max-w-full')
      this.#handleInitCropper(true)
    }

    this.element.addEventListener('attachment-manager-item:attach', this.#handleFileAttach)

    this.element.addEventListener('attachment-manager-item:detach', this.#handleFileDetach)
  }

  disconnect(): void {
    this.element.removeEventListener('attachment-manager-item:attach', this.#handleFileAttach)

    this.element.removeEventListener('attachment-manager-item:detach', this.#handleFileDetach)

    this.cropper?.destroy()
  }

  #handleInitCropper = (pushInitialState: boolean = false) => {
    // Destroy first
    if (this.sourceTarget.cropper) this.sourceTarget.cropper.destroy()

    this.cropper = new Cropper(this.sourceTarget, {
      aspectRatio: 1,
      initialAspectRatio: 1,
      rotatable: false,
      scalable: false,
      zoomable: false,
      ...(this.hasInitialStateValue &&
        pushInitialState && {
          data: this.initialStateValue,
        }),
      cropend: this.#handleCropEvent,
    })
    // Cropperjs takes it's sweet time to get the data, so force a small wait to set the initial computed values
    // There's no hook to get this data, so this ugly hack it's the most consistent solution
    setTimeout(this.#handleCropEvent, 500)
  }

  #handleCropEvent = () => {
    const { x: cropX, y: cropY, width: cropW, height: cropH } = this.cropper.getData()

    if (this.hasCropXTarget) this.cropXTarget.value = cropX.toString()
    if (this.hasCropYTarget) this.cropYTarget.value = cropY.toString()
    if (this.hasCropHTarget) this.cropHTarget.value = cropH.toString()
    if (this.hasCropWTarget) this.cropWTarget.value = cropW.toString()
  }

  #handleFileAttach = (event: AttachEvent) => {
    // If no file this present, it's just mounting. No setup needed
    if (!event.detail.file) return

    const fr = new FileReader()
    fr.onload = () => {
      this.sourceTarget.src = fr.result as string
      this.#handleInitCropper()
      if (this.hasSourceContainerTarget) this.sourceContainerTarget.classList.remove('hidden')
      if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden')
    }
    fr.readAsDataURL(event.detail.file)
  }

  #handleFileDetach = () => {
    this.cropper.destroy()
    if (this.hasSourceContainerTarget) this.sourceContainerTarget.classList.add('hidden')
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden')
  }
}
